import {
  COMPACT_TOPBAR_MEDIA,
  clearUtilityOverflowIds,
  getActiveOverflowCandidates,
  getCollapsibleOverflowCandidates,
  isFilterlessTopbarContext,
  overflowSetsEqual,
  pickNextOverflowId,
  pickRestoreOverflowId,
  pruneOverflowedIds,
  topbarContentFits
} from '@/utils/topbarOverflow';

export default {
  data() {
    return {
      overflowedIds: [],
      overflowMenuOpen: false,
      overflowObserver: null,
      overflowScheduleTimer: null,
      overflowMeasurePass: 0,
      overflowMeasureRaf: null,
      lastTopbarWidth: 0,
      overflowMeasureInProgress: false,
      compactHelpMql: null
    };
  },
  computed: {
    hasOverflowItems() {
      return this.overflowedIds.length > 0;
    },
    overflowContext() {
      return {
        showDashboardGeoFilters: this.showDashboardGeoFilters,
        showSearch: this.showSearch,
        showSearchCategory: this.showSearchCategory,
        showSelectFilter: this.showSelectFilter,
        showDateFilters: this.showDateFilters,
        showDateFiltersInLeading: this.showDateFiltersInLeading,
        showMonthYearFilters: this.showMonthYearFilters,
        showClearButton: this.showClearButton,
        singleDateFilter: this.singleDateFilter,
        isQmsOtpGuestSession: this.isQmsOtpGuestSession,
        isQmsPayrollClosureRole: this.isQmsPayrollClosureRole
      };
    }
  },
  watch: {
    overflowContext: {
      deep: true,
      handler() {
        this.ensureFilterlessUtilitiesInline();
        const candidateIds = getActiveOverflowCandidates(this.overflowContext);
        const pruned = pruneOverflowedIds(this.overflowedIds, candidateIds);
        if (!overflowSetsEqual(pruned, this.overflowedIds)) {
          this.overflowedIds = pruned;
        }
        this.scheduleOverflowMeasure();
      }
    },
    overflowedIds() {
      if (!this.hasOverflowItems) {
        this.overflowMenuOpen = false;
      }
    },
    displayNotificationCount() {
      this.scheduleOverflowMeasure();
    }
  },
  created() {
    // Forms without search/dates should show utilities inline on first paint.
    this.ensureFilterlessUtilitiesInline();
  },
  mounted() {
    this.$nextTick(() => {
      this.setupOverflowObserver();
      this.setupCompactHelpWatcher();
      this.ensureFilterlessUtilitiesInline();
      this.scheduleOverflowMeasure();
    });
  },
  beforeUnmount() {
    if (this.overflowObserver) {
      this.overflowObserver.disconnect();
    }
    this.destroyCompactHelpWatcher();
    if (this.overflowScheduleTimer) {
      clearTimeout(this.overflowScheduleTimer);
    }
    if (this.overflowMeasureRaf) {
      cancelAnimationFrame(this.overflowMeasureRaf);
    }
  },
  methods: {
    isOverflowed(id) {
      return this.overflowedIds.includes(id);
    },
    ensureFilterlessUtilitiesInline() {
      if (!isFilterlessTopbarContext(this.overflowContext)) {
        return;
      }
      const next = clearUtilityOverflowIds(this.overflowedIds);
      if (!overflowSetsEqual(next, this.overflowedIds)) {
        this.overflowedIds = next;
      }
    },
    setupCompactHelpWatcher() {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return;
      }
      this.compactHelpMql = window.matchMedia(COMPACT_TOPBAR_MEDIA);
      if (typeof this.compactHelpMql.addEventListener === 'function') {
        this.compactHelpMql.addEventListener('change', this.onCompactHelpChange);
      } else if (typeof this.compactHelpMql.addListener === 'function') {
        this.compactHelpMql.addListener(this.onCompactHelpChange);
      }
    },
    destroyCompactHelpWatcher() {
      if (!this.compactHelpMql) {
        return;
      }
      if (typeof this.compactHelpMql.removeEventListener === 'function') {
        this.compactHelpMql.removeEventListener('change', this.onCompactHelpChange);
      } else if (typeof this.compactHelpMql.removeListener === 'function') {
        this.compactHelpMql.removeListener(this.onCompactHelpChange);
      }
      this.compactHelpMql = null;
    },
    onCompactHelpChange() {
      this.ensureFilterlessUtilitiesInline();
      this.scheduleOverflowMeasure();
    },
    scheduleOverflowMeasure() {
      clearTimeout(this.overflowScheduleTimer);
      this.overflowScheduleTimer = setTimeout(() => {
        this.overflowMeasurePass += 1;
        this.runOverflowMeasure(this.overflowMeasurePass);
      }, 80);
    },
    runOverflowMeasure(passId) {
      const root = this.$refs.topbarRoot;
      if (!root) {
        return;
      }

      this.ensureFilterlessUtilitiesInline();

      const filterless = isFilterlessTopbarContext(this.overflowContext);
      // Filter-less forms: utilities are never collapsible (stay inline like image 2).
      const candidateIds = getCollapsibleOverflowCandidates(this.overflowContext);
      const activeIds = getActiveOverflowCandidates(this.overflowContext);
      let initialOverflow = pruneOverflowedIds(this.overflowedIds, activeIds);
      if (filterless) {
        initialOverflow = clearUtilityOverflowIds(initialOverflow);
      }
      const overflowed = new Set(initialOverflow);

      this.lastTopbarWidth = root.clientWidth || 0;
      this.overflowMeasureInProgress = true;

      const syncOverflowedList = () => {
        let next = [...overflowed];
        if (filterless) {
          next = clearUtilityOverflowIds(next);
          overflowed.clear();
          next.forEach(id => overflowed.add(id));
        }
        return next;
      };

      const finish = () => {
        const next = syncOverflowedList();
        if (!overflowSetsEqual(next, this.overflowedIds)) {
          this.overflowedIds = next;
        }
        this.overflowMeasureInProgress = false;
      };

      const applyState = callback => {
        const next = syncOverflowedList();
        if (!overflowSetsEqual(next, this.overflowedIds)) {
          this.overflowedIds = next;
        }
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            if (passId !== this.overflowMeasurePass) {
              this.overflowMeasureInProgress = false;
              return;
            }
            callback();
          });
        });
      };

      const optimizeOverflow = () => {
        if (!topbarContentFits(root)) {
          const nextId = pickNextOverflowId(candidateIds, overflowed);
          if (!nextId) {
            // Nothing left to collapse (utilities pinned inline on filter-less forms).
            finish();
            return;
          }
          overflowed.add(nextId);
          applyState(optimizeOverflow);
          return;
        }

        const restoreId = pickRestoreOverflowId(candidateIds, overflowed);
        if (!restoreId) {
          finish();
          return;
        }

        overflowed.delete(restoreId);
        applyState(() => {
          if (topbarContentFits(root)) {
            optimizeOverflow();
            return;
          }
          overflowed.add(restoreId);
          finish();
        });
      };

      applyState(optimizeOverflow);
    },
    measureOverflow() {
      this.scheduleOverflowMeasure();
    },
    setupOverflowObserver() {
      const root = this.$refs.topbarRoot;
      if (!root || typeof ResizeObserver === 'undefined') {
        return;
      }
      this.overflowObserver = new ResizeObserver(() => {
        if (this.overflowMeasureInProgress) {
          return;
        }
        if (this.overflowMeasureRaf) {
          cancelAnimationFrame(this.overflowMeasureRaf);
        }
        this.overflowMeasureRaf = requestAnimationFrame(() => {
          this.overflowMeasureRaf = null;
          this.scheduleOverflowMeasure();
        });
      });
      this.overflowObserver.observe(root);
      if (root.parentElement) {
        this.overflowObserver.observe(root.parentElement);
      }
    }
  }
};
